import { isMobile } from "./client_util"
import { isNative } from "./runtime_config"

/**
 * Fullscreen, and the little of it the browsers actually hand us.
 *
 * Two constraints shape everything here:
 *
 *  - It does not exist on iPhone. Every iOS browser is WebKit underneath, and WebKit only lets a
 *    <video> element go fullscreen there. It is also switched off when we are framed without
 *    allowfullscreen (the itch.io embed). `fullscreenSupported` is the check for both.
 *  - It cannot be entered on load. Every browser that supports it demands a transient user
 *    activation, so the earliest a phone can expand is the player's first tap -- which is what
 *    `enterFullscreenIfMobile` and `enterFullscreenOnFirstGesture` are for. Call them from
 *    inside a tap/click/key handler; outside one the request is refused and the activation is
 *    spent for nothing.
 */

const el = () => document.documentElement as any
const dom = () => document as any

const request = (): (() => unknown) | undefined => {
    const e = el()
    return (
        e.requestFullscreen?.bind(e) ||
        e.webkitRequestFullscreen?.bind(e) /* Safari */ ||
        e.msRequestFullscreen?.bind(e) /* IE11 */
    )
}

const release = (): (() => unknown) | undefined => {
    const d = dom()
    return d.exitFullscreen?.bind(d) || d.webkitExitFullscreen?.bind(d) || d.msExitFullscreen?.bind(d)
}

/** A refused request rejects rather than throws; none of this is worth an unhandled rejection. */
const swallow = (result: unknown) => {
    if (result && typeof (result as Promise<void>).catch === "function") {
        ;(result as Promise<void>).catch(() => undefined)
    }
}

export const fullscreenSupported = (): boolean => {
    // The native shell has no browser chrome to escape -- it launches fullscreen, with the status
    // bar hidden by Info.plist and the Android system bars by MainActivity. WebKit still exposes
    // requestFullscreen inside the WebView, so without this the login screen would draw an expand
    // chip that can only ever be a no-op. Same principle as the iPhone case below: better an
    // absent control than one that visibly lies.
    if (isNative()) {
        return false
    }
    if (typeof document === "undefined" || !request()) {
        return false
    }
    // false inside an iframe embedded without allowfullscreen.
    return dom().fullscreenEnabled ?? dom().webkitFullscreenEnabled ?? true
}

/**
 * True when the page is already running as an installed app rather than in a browser tab: a home
 * screen web app on iOS (`navigator.standalone`, which is the only signal there), or anything the
 * display-mode media query calls installed. Both are asked because iOS supports neither the other
 * one's answer: Safari has never matched `display-mode: standalone` reliably, and no other
 * platform has `navigator.standalone`.
 */
export const isStandalone = (): boolean => {
    if (typeof window === "undefined") {
        return false
    }
    if ((window.navigator as any)?.standalone === true) {
        return true
    }
    const match = window.matchMedia?.("(display-mode: standalone)")
    return !!match?.matches
}

/**
 * Whether to tell the player about Add to Home Screen.
 *
 * It is the complement of the expand chip, not an extra: exactly on the phones where there is no
 * fullscreen to ask for is installing the page the only way left to be rid of the browser's bars.
 * Which, today, means an iPhone. Not in the native shell -- it has no bars and nothing to install
 * -- and not once the player has already installed it.
 */
export const shouldOfferHomeScreenInstall = (): boolean =>
    isMobile() && !isNative() && !isStandalone() && !fullscreenSupported()

export const isFullscreen = (): boolean =>
    typeof document !== "undefined" &&
    !!(dom().fullscreenElement || dom().webkitFullscreenElement || dom().msFullscreenElement)

export const enterFullscreen = () => {
    if (!fullscreenSupported() || isFullscreen()) {
        return
    }
    swallow(request()!())
}

export const toggleFullscreen = () => {
    if (!fullscreenSupported()) {
        return
    }
    // Ask the document which way it is rather than keeping our own flag: Esc, the system back
    // gesture and the browser's own control all leave fullscreen without telling us, and a stale
    // flag turns the next tap into an exit that does nothing -- a button that looks dead.
    swallow(isFullscreen() ? release()?.() : request()!())
}

/**
 * How many gestures the arming below is allowed to spend. More than one because a request can be
 * refused for want of a user activation and says nothing when it is -- measured in Chrome, a
 * handler on `pointerup` can run a beat before the browser marks the activation, and the request
 * rejects with a bare "Permissions check failed". Few, because past the opening taps the player
 * is likelier to have left fullscreen deliberately than to have been refused, and expanding the
 * page under them would be its own kind of broken.
 */
const FIRST_GESTURE_ATTEMPTS = 3

/**
 * The same thing for the entries that never show a login screen (a saved name, an access token,
 * the itch build) and so have no Play button to hang it on: take the opening taps instead. This
 * is as close to "fullscreen on load" as the platform allows -- nobody may expand a page that
 * the player has not yet touched.
 */
export const enterFullscreenOnFirstGesture = () => {
    if (!isMobile() || !fullscreenSupported()) {
        return
    }

    // A single tap arrives as both a touchend and a pointerup. Whichever a browser sends first is
    // the family we keep, so the taps after it are counted once each.
    const events = ["touchend", "pointerup"]
    let attempts = 0

    const stop = () => events.forEach(type => window.removeEventListener(type, onGesture))

    function onGesture(this: void, event: Event) {
        events.filter(type => type !== event.type).forEach(type => window.removeEventListener(type, onGesture))

        if (isFullscreen()) {
            stop()
            return
        }
        attempts += 1
        enterFullscreen()
        if (attempts >= FIRST_GESTURE_ATTEMPTS) {
            stop()
        }
    }

    events.forEach(type => window.addEventListener(type, onGesture))
}

/**
 * Expand on a phone, called from a gesture we are already inside -- the tap that commits to
 * playing. The opening taps after it are armed too: a refused request says nothing, and once the
 * login screen is gone there is no fullscreen control left to try again with.
 *
 * Desktop is left alone. Yanking a windowed browser fullscreen because someone pressed Play is
 * not what they asked for.
 */
export const enterFullscreenIfMobile = () => {
    if (!isMobile()) {
        return
    }
    enterFullscreen()
    // Arm after the current gesture has finished, or the listener catches this very tap and
    // spends a retry on a request we have already made.
    setTimeout(enterFullscreenOnFirstGesture, 0)
}
